import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Eye, EyeOff, Pencil, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { TYPE_META } from "@/lib/types";

function IconBtn({ onClick, label, testId, children }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      aria-label={label}
      title={label}
      className="border border-hairline p-2 text-ink/60 transition-colors duration-200 hover:border-ink hover:text-ink"
    >
      {children}
    </button>
  );
}

export function DashboardCard({ card, index, onEdit }) {
  const [showPass, setShowPass] = useState(false);
  const meta = TYPE_META[card.type] || TYPE_META.other;

  let host = card.url;
  try {
    host = new URL(card.url).host;
  } catch (e) {
    /* keep raw url */
  }

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{
        duration: 0.6,
        delay: (index % 3) * 0.08,
        ease: [0.16, 1, 0.3, 1],
      }}
      data-testid="dashboard-card"
      className="group relative flex h-full flex-col border border-hairline bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-ink hover:shadow-[0_18px_40px_-24px_rgba(17,20,24,0.35)] md:p-7"
    >
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 h-[3px] w-0 transition-all duration-500 group-hover:w-full"
        style={{ background: meta.color }}
      />

      <div className="flex items-center justify-between gap-3">
        <span
          className="border px-2 py-1 font-mono text-[10px] tracking-[0.18em]"
          style={{
            color: meta.color,
            borderColor: `${meta.color}55`,
            background: `${meta.color}0D`,
          }}
        >
          {meta.label}
        </span>
        {card.plant ? (
          <span className="truncate font-mono text-[10px] uppercase tracking-[0.15em] text-ink/45">
            {card.plant}
          </span>
        ) : null}
      </div>

      <h3
        data-testid="card-name"
        className="mt-4 font-display text-xl font-bold tracking-tight"
      >
        {card.name}
      </h3>
      <a
        href={card.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="card-url-link"
        className="mt-1 inline-flex w-fit max-w-full items-center gap-1 font-mono text-xs text-ink/50 transition-colors duration-200 hover:text-signal"
      >
        <span className="truncate">{host}</span>
        <ArrowUpRight className="h-3 w-3 shrink-0" />
      </a>

      <div className="mt-5 space-y-3 border-t border-hairline pt-4">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="font-mono text-[10px] tracking-[0.2em] text-ink/40">
              USER
            </p>
            <p
              data-testid="card-username"
              className="truncate font-mono text-sm text-ink"
            >
              {card.username || "—"}
            </p>
          </div>
          {card.username ? (
            <IconBtn
              testId="copy-username-btn"
              label="Copy username"
              onClick={() => copy(card.username, "Username")}
            >
              <Copy className="h-3.5 w-3.5" />
            </IconBtn>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="font-mono text-[10px] tracking-[0.2em] text-ink/40">
              PASS
            </p>
            <p
              data-testid="card-password"
              className="truncate font-mono text-sm text-ink"
            >
              {card.password
                ? showPass
                  ? card.password
                  : "••••••••"
                : "—"}
            </p>
          </div>
          {card.password ? (
            <div className="flex items-center gap-1.5">
              <IconBtn
                testId="reveal-password-btn"
                label={showPass ? "Hide password" : "Show password"}
                onClick={() => setShowPass(!showPass)}
              >
                {showPass ? (
                  <EyeOff className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
              </IconBtn>
              <IconBtn
                testId="copy-password-btn"
                label="Copy password"
                onClick={() => copy(card.password, "Password")}
              >
                <Copy className="h-3.5 w-3.5" />
              </IconBtn>
            </div>
          ) : null}
        </div>
      </div>

      {card.notes ? (
        <p className="mt-3 text-sm leading-snug text-ink/55">{card.notes}</p>
      ) : null}

      <div className="mt-auto flex items-center gap-2 pt-5">
        <a
          href={card.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="open-dashboard-btn"
          className="inline-flex flex-1 items-center justify-center gap-2 bg-ink px-4 py-3 font-mono text-[11px] tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-signal"
        >
          LAUNCH DASHBOARD
          <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
        <button
          data-testid="edit-card-btn"
          onClick={() => onEdit(card)}
          aria-label="Edit card"
          title="Edit card"
          className="border border-hairline p-3 text-ink/60 transition-colors duration-200 hover:border-ink hover:text-ink"
        >
          <Pencil className="h-4 w-4" />
        </button>
      </div>
    </motion.article>
  );
}
